import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import { ToggleSwitch } from '@/components/ui/toggle-switch'

import { ADMIN_OR_PM } from '../../Can'
import API from '../../../api/api'
import { getUpdateClientBody, getNewClientValidation } from '../helpers'
import Icon from '../../Icons'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import SelectListWindow from '../../Popups/SelectListWindow'
import InfiniteScroll from '../../InfiniteScroll/InfiniteScroll'

import MailingListsSection from './MailingListsSection'
import GeneralInformationSection from './GeneralInformationSection'
import IntegrationsSection from './IntegrationsSection/IntegrationsSection'
import ShowcaseSection from './ShowcaseSection'
import RecentActivity from './RecentActivity'
import TagsSection from './TagsSection'
import { useGuestPasses } from './hooks'
import { GuestPassesSection } from './GuestPassesSection'
import { VisitsSection } from './VisitsSection'

const RECENT_ACTIVITIES_LIMIT = 30
const FIELD_TO_EDIT_MODE_KEY = {
  firstName: 'name',
  lastName: 'name',
  phone: 'phone',
}
const getActivityTypes = (t) => [
  {
    value: 'reservation',
    label: t('clients-legacy:profileView.recentActivities.activityTypes.reservations'),
  },
  { value: 'guest', label: t('clients-legacy:profileView.recentActivities.activityTypes.guest') },
  {
    value: 'maintenance',
    label: t('clients-legacy:profileView.recentActivities.activityTypes.maintenance'),
  },
  {
    value: 'post',
    label: t('clients-legacy:profileView.recentActivities.activityTypes.socialFeed'),
  },
  { value: 'note', label: t('clients-legacy:profileView.recentActivities.activityTypes.notes') },
  {
    value: 'package',
    label: t('clients-legacy:profileView.recentActivities.activityTypes.package'),
  },
  {
    value: 'allActivities',
    label: t('clients-legacy:profileView.recentActivities.activityTypes.allActivities'),
  },
]

const ClientProfileView = ({
  clientData,
  userRole,
  buildings,
  closeProfileView,
  showUserStatusPopup,
  form,
  reduceAllClientsCountByOne,
  buildingId,
  onUIToggle,
}) => {
  const { t } = useTranslation(['clients-legacy', 'clients', 'common'])
  const activityTypes = useMemo(() => getActivityTypes(t), [t])
  const [activity, setActivity] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [newClientData, setNewClientData] = useState(clientData)
  const [buildingCode, setBuildingCode] = useState(newClientData.buildingId?.code)
  const [deletePending, setDeletePending] = useState(false)
  const [recentActivities, setRecentActivities] = useState([])
  const [skip, setSkip] = useState(0)
  const [editModeList, setEditModeList] = useState([])
  const [isEditingMailingListMode, setIsEditingMailingListMode] = useState(false)
  const timeout = useRef(null)

  const building = buildings.find(({ id }) => id === buildingId)
  const amenityPassesAvailable = building?.amenityPassesAvailable

  const {
    data: guestPassesData,
    isLoading: guestPassesDataLoading,
    fetchMore: fetchGuestPassVisits,
    addGuestPass,
    hasMore: hasMoreGuestPassVisits,
  } = useGuestPasses(clientData.id, { enabled: !!amenityPassesAvailable })

  const [availableBuildingMailingList, setAvailableBuildingMailingList] = useState([])
  const [subscribedClientMailingList, setSubscribedClientMailingList] = useState([])
  const [newSubClientMailingList, setNewSubClientMailingList] = useState([])
  const [visibleMailingListPopup, setVisibleMailingListPopup] = useState(false)

  const timezone = newClientData.buildingId?.timezone
  const isDisabled = userRole === 'Trainer'
  const canAccessKeyless = ADMIN_OR_PM.includes(userRole)
  const { avatar } = form.clientProfile
  const { realPagePropertyId, entrataPropertyId, yardiPropertyId, resmanPropertyId } =
    building || {}

  const isPMSIntegrated = !!(
    realPagePropertyId ||
    entrataPropertyId ||
    yardiPropertyId ||
    resmanPropertyId
  )

  const newClientValidation = useMemo(() => getNewClientValidation(t), [t])
  const validationSchema = Yup.object().shape({
    firstName: newClientValidation.requiredString,
    lastName: newClientValidation.requiredString,
    email: newClientValidation.requiredEmail,
    phone: newClientValidation.phone,
    street: newClientValidation.requiredString,
    apartment: newClientValidation.requiredString,
    zipCode: newClientValidation.zipCode,
    moveInDate: newClientValidation.moveInDate,
    moveOutDate: newClientValidation.moveOutDate,
    leaseStart: newClientValidation.leaseStart,
    leaseEnd: newClientValidation.leaseEnd,
    birth: newClientValidation.birth,
  })

  const selectBuilding = ({ code }) => {
    setBuildingCode(code)
  }

  const cancelAllChanges = (resetForm) => {
    setEditModeList([])
    resetForm()
    selectBuilding(newClientData.buildingId)
  }

  const handleUpdateClient = async (values, { setSubmitting }) => {
    const { id: clientId } = clientData
    const body = getUpdateClientBody(values, avatar, editModeList, timezone)

    setSubmitting(true)
    API.updateUserData(body, clientId, 'client')
      .then((res) => {
        const _res = { ...res }

        _res.tags = Array.isArray(_res.tags)
          ? _res.tags.map((item) => (typeof item === 'string' ? item : item.name))
          : []
        setNewClientData(_res)
        showUserStatusPopup({ status: 'updated' })
        setEditModeList([])
        setSubmitting(false)
      })
      .catch((err) => {
        if (err === 404) {
          setErrorMessage(t('clients-legacy:profileView.buildingCodeError'))
          timeout.current = setTimeout(() => {
            setErrorMessage(null)
          }, 2000)
          setSubmitting(false)
        } else {
          setErrorMessage(t('clients-legacy:newClientForm.serverError'))
          timeout.current = setTimeout(() => {
            setErrorMessage(null)
          }, 2000)
          setSubmitting(false)
        }
      })
  }

  const handleUpdateClientActiveStatus = (active, setFieldValue, setSubmitting) => {
    const { id: clientId } = clientData

    const notAllowed =
      active &&
      isPMSIntegrated &&
      newClientData.statusPMS &&
      newClientData.statusPMS.status === 'Past' &&
      ['Administrator', 'PropertyManager', 'ShiftManager'].includes(userRole)

    if (notAllowed) {
      setErrorMessage(t('clients-legacy:profileView.settings.pastStatusActivationError'))
    } else {
      setSubmitting(true)
      API.updateUserData({ active }, clientId, 'client')
        .then((res) => {
          setFieldValue('active', res.active)
          setNewClientData((prev) => ({ ...prev, active: res.active }))
          showUserStatusPopup({ status: 'updated' })
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.message
          setErrorMessage(message || t('clients-legacy:newClientForm.serverError'))
          timeout.current = setTimeout(() => {
            setErrorMessage(null)
          }, 2000)
        })
        .finally(() => setSubmitting(false))
    }
  }

  const handleDelete = (withDeletePending = true) => {
    withDeletePending && setDeletePending(true)
    return API.deleteClient(clientData.user?.id)
      .then(() => {
        setEditModeList([])
        setDeletePending(false)
        closeProfileView()
        showUserStatusPopup({ status: 'deleted' })
      })
      .catch((error) => {
        if (withDeletePending) {
          setDeletePending(false)
          setErrorMessage(t('clients-legacy:profileView.deleteClientError'))
          timeout.current = setTimeout(() => {
            setErrorMessage(null)
          }, 2000)
        } else {
          throw error
        }
      })
  }

  const rejectClient = () => {
    setDeletePending(true)
    API.declineClient(clientData.id)
      .then(() => {
        setEditModeList([])
        closeProfileView()
        reduceAllClientsCountByOne()
        showUserStatusPopup({ status: 'rejected' })
        setDeletePending(false)
      })
      .catch(() => {
        setErrorMessage(t('clients-legacy:profileView.rejectClientError'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
        setDeletePending(false)
      })
  }

  const confirmClient = () => {
    setDeletePending(true)
    API.activateClient(clientData.id)
      .then(({ isActive }) => {
        setNewClientData({ ...newClientData, user: { ...newClientData.user, isActive } })
        showUserStatusPopup({ status: 'confirmed' })
        reduceAllClientsCountByOne()
        setDeletePending(false)
      })
      .catch(() => {
        setErrorMessage(t('clients-legacy:profileView.activateClientError'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
        setDeletePending(false)
      })
  }

  useEffect(() => {
    if (avatar.file) {
      setEditModeList([...editModeList, 'avatar'])
    } else {
      setEditModeList(editModeList.filter((x) => x !== 'avatar'))
    }
  }, [avatar])

  const setNewSubMailingList = (setSubmitting) => {
    setSubmitting(true)
    API.setNewSubMailingListForClient(newClientData.id, {
      subscribe: newSubClientMailingList.map((item) => item.id),
    })
      .then(() => {
        showUserStatusPopup({ status: 'updated' })
        setSubscribedClientMailingList(newSubClientMailingList)
        setIsEditingMailingListMode(false)
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => setSubmitting(false))
  }

  useEffect(() => {
    API.getMailingListForBuilding(newClientData.buildingId?.id)
      .then((res) => setAvailableBuildingMailingList(res))
      .catch((err) => {
        console.error(err)
      })
  }, [newClientData.buildingId?.id])

  useEffect(() => {
    API.getClientSubscribedMailingList(newClientData.id)
      .then((list) => {
        setSubscribedClientMailingList(list)
        setNewSubClientMailingList(list)
      })
      .catch((err) => {
        console.error(err)
      })
  }, [newClientData.id])

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  const getRecentActivityData = (id, limit, skip, activity) => {
    API.getClientRecentActivities(id, limit, skip, activity)
      .then((data) => {
        setRecentActivities([...recentActivities, ...data])
      })
      .catch(() => {
        setErrorMessage(t('clients-legacy:profileView.loadActivitiesError'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      })
  }

  useEffect(() => {
    if (recentActivities.length === skip) {
      getRecentActivityData(clientData.id, RECENT_ACTIVITIES_LIMIT, skip, activity)
    }
  }, [activity, skip])

  const handleChangeActivity = (e) => {
    setSkip(0)
    setActivity(e.target.value)
    setRecentActivities([])
  }

  const handleEndReach = () => {
    setSkip(skip + RECENT_ACTIVITIES_LIMIT)
  }

  return (
    <div className="ctp-clients__client-profile-view">
      {visibleMailingListPopup && (
        <SelectListWindow
          availableList={availableBuildingMailingList.filter(
            (item) => ![...newSubClientMailingList.map((x) => x.id)].includes(item.id),
          )}
          onCancel={() => setVisibleMailingListPopup(false)}
          onChoose={(selected) => {
            setNewSubClientMailingList([...newSubClientMailingList, selected])
            setIsEditingMailingListMode(true)
            setVisibleMailingListPopup(false)
          }}
        />
      )}
      <div className={`left-side ${!isDisabled ? '' : 'full-screen'}`}>
        <Formik
          enableReinitialize={true}
          initialValues={{
            firstName: newClientData.user?.firstName || '',
            lastName: newClientData.user?.lastName || '',
            email: newClientData.user?.email || '',
            phone: newClientData.phone ? String(newClientData.phone) : null,
            street: newClientData.street || '',
            apartment: newClientData.apartment || '',
            parkingSpotInfo: newClientData.parkingSpotInfo || '',
            zipCode: newClientData.zipCode || '',
            moveInDate: newClientData.moveInDate
              ? toZonedTime(newClientData.moveInDate, timezone)
              : null,
            moveOutDate: newClientData.moveOutDate
              ? toZonedTime(newClientData.moveOutDate, timezone)
              : null,
            leaseStart: newClientData.leaseStart
              ? toZonedTime(newClientData.leaseStart, timezone)
              : null,
            leaseEnd: newClientData.leaseEnd ? toZonedTime(newClientData.leaseEnd, timezone) : null,
            birth: newClientData.birth ? toZonedTime(newClientData.birth, timezone) : null,
            code: newClientData.buildingId?.code || '',
            pets: newClientData.pets,
            active: newClientData.active,
            buildingNumber: newClientData.buildingNumber,
            floor: newClientData.floor,
            clientAvatar: '',
            tags: Array.isArray(newClientData.tags)
              ? newClientData.tags.map((t) => (typeof t === 'string' ? t : t.name))
              : [],
            inHouse: newClientData.inHouse || false,
          }}
          validationSchema={validationSchema}
          onSubmit={handleUpdateClient}
        >
          {({
            handleChange,
            values,
            errors,
            touched,
            setFieldTouched,
            dirty,
            isSubmitting,
            handleSubmit,
            setFieldValue,
            resetForm,
            setSubmitting,
            validateForm,
            setTouched,
          }) => {
            const clearField = (name) => {
              setFieldValue(`${name}`, '')
              setFieldTouched(`${name}`)
            }

            const handleSave = async () => {
              const errors = await validateForm()
              if (Object.keys(errors).length > 0) {
                setTouched(Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}))
                const keysToActivate = [
                  ...new Set(
                    Object.keys(errors)
                      .map((field) => FIELD_TO_EDIT_MODE_KEY[field] ?? field)
                      .filter((key) => !editModeList.includes(key)),
                  ),
                ]
                if (keysToActivate.length > 0) {
                  setEditModeList((prev) => [...prev, ...keysToActivate])
                }
                setErrorMessage(t('clients-legacy:profileView.saveErrorsMessage'))
                timeout.current = setTimeout(() => setErrorMessage(null), 3000)
                return
              }
              handleSubmit()
            }

            return (
              <React.Fragment>
                <div className="top-bar">
                  <div className={`top-bar__inner ${editModeList.length ? 'in-edit-mode' : ''}`}>
                    <div
                      className="back-button"
                      onClick={() => {
                        setEditModeList([])
                        closeProfileView()
                      }}
                    >
                      <Icon icon="arrow" />
                      <span>{t('clients-legacy:profileView.backButton')}</span>
                    </div>
                    {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
                    {editModeList.length ? (
                      <div className="buttons">
                        <button
                          type="button"
                          className={`button button-gray ${isSubmitting ? 'is-loading' : ''}`}
                          onClick={() => cancelAllChanges(resetForm)}
                          disabled={isSubmitting || isDisabled}
                        >
                          {t('common:actions.cancel')}
                        </button>
                        <button
                          type="button"
                          className={`button button-primary auto-width ${
                            isSubmitting ? 'is-loading' : ''
                          }`}
                          onClick={handleSave}
                          disabled={!dirty || isSubmitting || isDisabled}
                        >
                          {t('common:actions.save')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="client-content">
                  <React.Fragment>
                    <ShowcaseSection
                      editModeList={editModeList}
                      isDisabled={isDisabled}
                      userRole={userRole}
                      isSubmitting={isSubmitting}
                      values={values}
                      errors={errors}
                      touched={touched}
                      newClientData={newClientData}
                      deletePending={deletePending}
                      confirmClient={confirmClient}
                      rejectClient={rejectClient}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      setEditModeList={setEditModeList}
                      setFieldTouched={setFieldTouched}
                      clearField={clearField}
                      handleUpdateClientActiveStatus={handleUpdateClientActiveStatus}
                      setSubmitting={setSubmitting}
                      handleDelete={handleDelete}
                    />
                    <GeneralInformationSection
                      userRole={userRole}
                      values={values}
                      editModeList={editModeList}
                      buildings={buildings}
                      buildingCode={buildingCode}
                      isSubmitting={isSubmitting}
                      isDisabled={isDisabled}
                      isPMSIntegrated={clientData.buildingId?.PMS}
                      touched={touched}
                      errors={errors}
                      newClientData={newClientData}
                      handleChange={handleChange}
                      setFieldTouched={setFieldTouched}
                      setFieldValue={setFieldValue}
                      clearField={clearField}
                      setEditModeList={setEditModeList}
                      selectBuilding={selectBuilding}
                    />
                    <TagsSection
                      tags={values.tags}
                      setEditModeList={setEditModeList}
                      setFieldValue={setFieldValue}
                      buildingId={buildingId}
                    />
                    {amenityPassesAvailable ? (
                      <React.Fragment>
                        <GuestPassesSection
                          clientId={clientData.id}
                          isLoading={guestPassesDataLoading}
                          totalGuestPasses={guestPassesData.totalGuestPasses}
                          usedGuestPasses={guestPassesData.usedGuestPasses}
                        />
                        <VisitsSection
                          addGuestPass={addGuestPass}
                          amenityClientId={guestPassesData.id}
                          clientId={clientData.id}
                          fetchGuestPassVisits={fetchGuestPassVisits}
                          guestPassVisits={guestPassesData.guestPassVisits}
                          isLoading={guestPassesDataLoading}
                          showMoreButton={hasMoreGuestPassVisits}
                          timezone={building.timezone}
                          clientBuildingId={clientData.buildingId.id}
                        />
                      </React.Fragment>
                    ) : null}
                    {canAccessKeyless && (
                      <IntegrationsSection
                        isSubmitting={isSubmitting}
                        clientData={newClientData}
                        onClientDataChange={setNewClientData}
                      />
                    )}
                    <MailingListsSection
                      isEditing={isEditingMailingListMode}
                      isSubmitting={isSubmitting}
                      isDisabled={isDisabled}
                      subscribedClientMailingList={subscribedClientMailingList}
                      newSubClientMailingList={newSubClientMailingList}
                      availableBuildingMailingList={availableBuildingMailingList}
                      setNewSubClientMailingList={setNewSubClientMailingList}
                      setVisibleMailingListPopup={setVisibleMailingListPopup}
                      setIsEditingMailingListMode={setIsEditingMailingListMode}
                      setNewSubMailingList={() => setNewSubMailingList(setSubmitting)}
                    />
                  </React.Fragment>
                </div>
              </React.Fragment>
            )
          }}
        </Formik>
      </div>

      {!isDisabled && (
        <div className="right-side">
          <div className="recent-activities-header">
            <div className="title">{t('clients-legacy:profileView.recentActivities.title')}</div>
            <SelectField
              name={'activities'}
              value={activity}
              placeholder={t(
                'clients-legacy:profileView.recentActivities.activityTypes.allActivities',
              )}
              options={activityTypes}
              onChange={handleChangeActivity}
              placeholderStyle={{ color: '#777777' }}
            />
            {onUIToggle && (
              <ToggleSwitch
                falsyLabel={t('clients:list.designToggle.old')}
                truthyLabel={t('clients:list.designToggle.new')}
                checked={false}
                onChange={onUIToggle}
              />
            )}
          </div>
          {recentActivities.length ? (
            <div className="recent-activities-list">
              <InfiniteScroll
                data={recentActivities}
                renderItem={(item) => <RecentActivity timezone={timezone} recentActivity={item} />}
                onEndReached={handleEndReach}
                style={{ height: `calc(100vh - 224px)` }}
                isLoadedAll={recentActivities.length === skip}
              />
            </div>
          ) : (
            <div className="recent-activities-content">
              {t('clients-legacy:profileView.noResults')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

ClientProfileView.propTypes = {
  clientData: PropTypes.object,
  userRole: PropTypes.string,
  closeProfileView: PropTypes.func,
  onUIToggle: PropTypes.func,
  showUserStatusPopup: PropTypes.func,
  reduceAllClientsCountByOne: PropTypes.func,
  form: PropTypes.object,
  buildings: PropTypes.array,
  buildingId: PropTypes.number,
}

export default connect(
  (state) => ({
    form: state.form,
  }),
  {},
)(ClientProfileView)
